import { useEffect, useRef, useState } from 'react';
import type { StreamEvent } from '@hypevault/shared-types';

type Rarity = 'common' | 'rare' | 'epic' | 'legendary' | 'mythic';

const RARITY_COLORS: Record<Rarity, { base: string; glow: string; label: string }> = {
  common: { base: '#9ca3af', glow: 'rgba(156,163,175,0.55)', label: 'Common' },
  rare: { base: '#3b82f6', glow: 'rgba(59,130,246,0.6)', label: 'Rare' },
  epic: { base: '#a855f7', glow: 'rgba(168,85,247,0.65)', label: 'Epic' },
  legendary: { base: '#f59e0b', glow: 'rgba(245,158,11,0.7)', label: 'Legendary' },
  mythic: { base: '#ef4444', glow: 'rgba(239,68,68,0.75)', label: 'Mythic' },
};

interface QueueItem {
  id: string;
  viewerName: string;
  cardName: string;
  cardImageUrl: string | null;
  rarity: Rarity;
  description: string | null;
  message: string | null;
  // Per-card add-ons set in the card editor's Add-ons step. The widget plays
  // them on the same clock as the flip animation; chatMessage is purely
  // server-side and isn't surfaced here.
  cardSoundUrl: string | null;
  cardTtsMessage: string | null;
}

export function CardAlertWidget({
  fields,
  latestEvent,
}: {
  fields: Record<string, unknown>;
  latestEvent: StreamEvent | null;
}) {
  const durationMs = Number(fields.duration ?? 7) * 1000;
  const soundEnabled = (fields.soundEnabled as boolean | undefined) ?? true;
  const soundUrl = (fields.soundUrl as string | undefined) ?? '/sounds/banner-alert.wav';
  const showMessage = (fields.showMessage as boolean | undefined) ?? true;
  const showDescription = (fields.showDescription as boolean | undefined) ?? true;
  const minRarity = (fields.minRarity as Rarity | undefined) ?? null;

  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [current, setCurrent] = useState<QueueItem | null>(null);
  const [phase, setPhase] = useState<'in' | 'out'>('in');
  const lastId = useRef<string | null>(null);

  useEffect(() => {
    if (!latestEvent || latestEvent.id === lastId.current) return;
    if (latestEvent.type !== 'cardRedemption') return;
    lastId.current = latestEvent.id;

    const rarity = (latestEvent.cardRarity ?? 'common') as Rarity;
    if (minRarity) {
      const order: Rarity[] = ['common', 'rare', 'epic', 'legendary', 'mythic'];
      if (order.indexOf(rarity) < order.indexOf(minRarity)) return;
    }

    setQueue((q) => [
      ...q,
      {
        id: latestEvent.id,
        viewerName: latestEvent.viewerName,
        cardName: latestEvent.cardName,
        cardImageUrl: latestEvent.cardImageUrl ?? null,
        rarity,
        description: latestEvent.cardDescription ?? null,
        message: latestEvent.cardMessage ?? null,
        cardSoundUrl: latestEvent.cardSoundUrl ?? null,
        cardTtsMessage: latestEvent.cardTtsMessage ?? null,
      },
    ]);
  }, [latestEvent, minRarity]);

  // Drain queue → current
  useEffect(() => {
    if (current || queue.length === 0) return;
    const next = queue[0]!;
    setQueue((q) => q.slice(1));
    setCurrent(next);
    setPhase('in');
    // CSS holds the flip on the back face for 1s before animating; line up
    // all reveal-audio (banner sound, per-card SFX, TTS) with the actual
    // flip so they land together.
    const playOnReveal = () => {
      if (soundEnabled) {
        try {
          const audio = new Audio(soundUrl);
          audio.volume = 0.5;
          void audio.play().catch(() => {});
        } catch {
          /* ignore */
        }
      }
      // Per-card add-on sound — independent of the widget's banner sound,
      // streamers can have a "WAKE UP" siren on a specific card.
      if (next.cardSoundUrl) {
        try {
          const audio = new Audio(next.cardSoundUrl);
          audio.volume = 0.7;
          void audio.play().catch(() => {});
        } catch {
          /* ignore */
        }
      }
      // Per-card add-on TTS via the browser Web Speech API. No extra deps,
      // works in OBS browser source. Speech kicks slightly after the sound
      // so it doesn't trample the SFX.
      if (next.cardTtsMessage && typeof window !== 'undefined' && 'speechSynthesis' in window) {
        try {
          const utter = new SpeechSynthesisUtterance(next.cardTtsMessage);
          utter.volume = 1;
          utter.rate = 1;
          utter.pitch = 1;
          window.speechSynthesis.cancel();
          setTimeout(() => window.speechSynthesis.speak(utter), 250);
        } catch {
          /* ignore */
        }
      }
    };
    const timer = setTimeout(playOnReveal, 1000);
    return () => clearTimeout(timer);
  }, [current, queue, soundEnabled, soundUrl]);

  // Schedule fade-out
  useEffect(() => {
    if (!current) return;
    const tOut = setTimeout(() => setPhase('out'), durationMs - 500);
    const tDone = setTimeout(() => setCurrent(null), durationMs);
    return () => {
      clearTimeout(tOut);
      clearTimeout(tDone);
    };
  }, [current, durationMs]);

  if (!current) return null;
  const rc = RARITY_COLORS[current.rarity];

  const cssVars: React.CSSProperties = {
    ['--ca-color' as string]: rc.base,
    ['--ca-glow' as string]: rc.glow,
  };

  return (
    <div className={`ca-root phase-${phase}`} style={cssVars}>
      <div className="ca-stage">
        <div className="ca-card">
          <div className="ca-card-inner">
            <div className="ca-card-face ca-card-back">
              <div className="ca-back-art" />
              <div className="ca-back-emblem">HV</div>
            </div>
            <div className="ca-card-face ca-card-front">
              {current.cardImageUrl ? (
                <img alt="" src={current.cardImageUrl} className="ca-art" />
              ) : (
                <div className="ca-art-fallback">🃏</div>
              )}
              <div className="ca-card-overlay">
                <div className="ca-rarity-pill">{rc.label}</div>
                <div className="ca-card-name">{current.cardName}</div>
                {showDescription && current.description && (
                  <div className="ca-card-desc">{current.description}</div>
                )}
              </div>
              <div className="ca-shine" />
            </div>
          </div>
          <div className="ca-card-glow" />
        </div>
        <div className="ca-text">
          <div className="ca-viewer">{current.viewerName}</div>
          <div className="ca-played">played a card</div>
          {showMessage && current.message && (
            <div className="ca-message">&ldquo;{current.message}&rdquo;</div>
          )}
        </div>
      </div>
      <CardAlertStyles />
    </div>
  );
}

function CardAlertStyles() {
  return (
    <style>{`
      .ca-root {
        width: 100%;
        height: 100%;
        position: relative;
        font-family: 'Inter', system-ui, sans-serif;
        pointer-events: none;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .ca-stage {
        display: flex;
        align-items: center;
        gap: 32px;
        padding: 24px;
        opacity: 0;
        transform: translateY(20px) scale(0.92);
      }
      .ca-root.phase-in .ca-stage { animation: ca-stage-in 0.7s cubic-bezier(0.16,1,0.3,1) forwards; }
      .ca-root.phase-out .ca-stage { animation: ca-stage-out 0.5s ease-in forwards; }
      @keyframes ca-stage-in {
        0%   { opacity: 0; transform: translateY(20px) scale(0.92); }
        100% { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes ca-stage-out {
        0%   { opacity: 1; transform: translateY(0) scale(1); }
        100% { opacity: 0; transform: translateY(-12px) scale(0.94); filter: blur(4px); }
      }

      .ca-card {
        position: relative;
        width: 200px;
        aspect-ratio: 5 / 7;
        perspective: 1200px;
        flex-shrink: 0;
      }
      .ca-card-inner {
        position: absolute;
        inset: 0;
        transform-style: preserve-3d;
        transform: rotateY(180deg);
      }
      .ca-root.phase-in .ca-card-inner {
        animation: ca-flip 1.1s 1s cubic-bezier(0.55,0.05,0.2,1) forwards;
      }
      @keyframes ca-flip {
        0%   { transform: rotateY(180deg); }
        100% { transform: rotateY(0deg); }
      }
      .ca-card-face {
        position: absolute;
        inset: 0;
        border-radius: 14px;
        backface-visibility: hidden;
        overflow: hidden;
        box-shadow:
          0 16px 38px -10px rgba(0,0,0,0.6),
          0 0 0 1px rgba(255,255,255,0.08) inset;
      }
      .ca-card-back {
        background:
          radial-gradient(circle at 50% 35%, var(--ca-color) 0%, rgba(0,0,0,0.65) 70%),
          repeating-linear-gradient(135deg, rgba(255,255,255,0.04) 0 6px, transparent 6px 12px);
        border: 2px solid var(--ca-color);
        display: grid;
        place-items: center;
        /* Back face sits on the reverse so the flip reveals the front (art). */
        transform: rotateY(180deg);
      }
      .ca-back-art {
        position: absolute;
        inset: 12px;
        border: 1px dashed rgba(255,255,255,0.25);
        border-radius: 8px;
      }
      .ca-back-emblem {
        position: relative;
        color: white;
        font-weight: 900;
        font-size: 38px;
        letter-spacing: -2px;
        text-shadow: 0 0 18px var(--ca-glow), 0 2px 6px rgba(0,0,0,0.6);
      }
      .ca-card-front {
        background: #0b0918;
        border: 2px solid var(--ca-color);
      }
      .ca-art {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .ca-art-fallback {
        width: 100%;
        height: 100%;
        display: grid;
        place-items: center;
        font-size: 72px;
        background:
          radial-gradient(circle at 50% 40%, var(--ca-color), rgba(0,0,0,0.7));
      }
      .ca-card-overlay {
        position: absolute;
        left: 0; right: 0; bottom: 0;
        padding: 28px 12px 12px;
        background: linear-gradient(180deg, transparent, rgba(0,0,0,0.85));
        color: white;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .ca-rarity-pill {
        align-self: flex-start;
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 1.2px;
        text-transform: uppercase;
        padding: 3px 7px;
        border-radius: 4px;
        background: var(--ca-color);
        color: #0b0918;
        box-shadow: 0 0 12px var(--ca-glow);
      }
      .ca-card-name {
        font-size: 15px;
        font-weight: 800;
        line-height: 1.15;
        text-shadow: 0 2px 6px rgba(0,0,0,0.8);
      }
      .ca-card-desc {
        font-size: 10px;
        line-height: 1.3;
        color: rgba(255,255,255,0.75);
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .ca-shine {
        position: absolute;
        inset: 0;
        background: linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%);
        transform: translateX(-120%);
        pointer-events: none;
      }
      .ca-root.phase-in .ca-shine {
        animation: ca-shine 1.4s 2.1s ease-out forwards;
      }
      @keyframes ca-shine {
        0%   { transform: translateX(-120%); }
        100% { transform: translateX(120%); }
      }
      .ca-card-glow {
        position: absolute;
        inset: -14px;
        border-radius: 22px;
        box-shadow: 0 0 60px 6px var(--ca-glow);
        opacity: 0;
        pointer-events: none;
        z-index: -1;
      }
      .ca-root.phase-in .ca-card-glow {
        animation: ca-card-glow 1.6s 1.8s ease-out forwards;
      }
      @keyframes ca-card-glow {
        0%   { opacity: 0; }
        40%  { opacity: 1; }
        100% { opacity: 0.55; }
      }

      .ca-text {
        color: white;
        min-width: 0;
        max-width: 360px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        opacity: 0;
        transform: translateX(-12px);
      }
      .ca-root.phase-in .ca-text {
        animation: ca-text-in 0.6s 2s ease-out forwards;
      }
      @keyframes ca-text-in {
        from { opacity: 0; transform: translateX(-12px); }
        to   { opacity: 1; transform: translateX(0); }
      }
      .ca-viewer {
        font-size: 28px;
        font-weight: 900;
        letter-spacing: -0.5px;
        text-shadow: 0 2px 10px rgba(0,0,0,0.6), 0 0 18px var(--ca-glow);
        word-break: break-word;
        line-height: 1.05;
      }
      .ca-played {
        font-size: 13px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 1.5px;
        color: var(--ca-color);
      }
      .ca-message {
        margin-top: 8px;
        font-size: 14px;
        font-style: italic;
        color: rgba(255,255,255,0.85);
        line-height: 1.35;
        word-break: break-word;
      }
    `}</style>
  );
}
